// BARBER PUSH (Phase 2, Jose 2026-10-24) — registers the staff device's FCM token into
// `barberPushTokens`, tagged with the pinned-in barberId, so the operator-alert Cloud Function can push
// "your client has arrived" to that barber's phone. This is the WEB path (staff web app / installed PWA);
// the native (TestFlight) app will register the same way into the same collection once the Capacitor
// messaging plugin is added + a new build ships — the Cloud Function is channel-agnostic.
//
// SAFETY: never auto-prompt for permission (a denied prompt is sticky). enableBarberPush() runs only on an
// explicit tap; initBarberPush() is the SILENT re-register for a device that already granted permission.
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import app, { db } from '../config/firebase';

const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

export type BarberPushState = 'unsupported' | 'no-key' | 'denied' | 'default' | 'granted';

export function barberPushState(): BarberPushState {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('Notification' in window) || !('PushManager' in window)) return 'unsupported';
  if (!VAPID) return 'no-key';
  return Notification.permission as BarberPushState;
}

async function registerToken(barberId: string, barberName?: string): Promise<boolean> {
  const { isSupported, getMessaging, getToken, onMessage } = await import('firebase/messaging');
  if (!(await isSupported())) return false;

  // The one static /firebase-messaging-sw.js serves prod + staging — pass this env's config in the query.
  const cfg = app.options as any;
  const qs = new URLSearchParams({
    apiKey: cfg.apiKey || '',
    projectId: cfg.projectId || '',
    messagingSenderId: cfg.messagingSenderId || '',
    appId: cfg.appId || '',
  }).toString();
  const swReg = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${qs}`);

  const messaging = getMessaging(app);
  const token = await getToken(messaging, { vapidKey: VAPID, serviceWorkerRegistration: swReg });
  if (!token) return false;

  await setDoc(doc(db, 'barberPushTokens', token), {
    barberId, barberName: barberName || null, token, platform: 'web', app: 'staff', updatedAt: serverTimestamp(),
  }, { merge: true });

  // The in-app popup (OperatorAlertPopup) already shows foreground alerts via the Firestore listener, so
  // we don't double-notify here; just log. The SW handles background/locked pushes.
  onMessage(messaging, () => { /* foreground handled by the in-app popup */ });

  localStorage.setItem('slik_barber_push_token', token);
  return true;
}

/** Explicit opt-in (button tap): request permission, then register this device for the barber. */
export async function enableBarberPush(barberId: string, barberName?: string): Promise<BarberPushState> {
  if (!barberId) return 'unsupported';
  const st = barberPushState();
  if (st === 'unsupported' || st === 'no-key') return st;
  if (Notification.permission === 'default') {
    const p = await Notification.requestPermission();
    if (p !== 'granted') return p as BarberPushState;
  } else if (Notification.permission === 'denied') {
    return 'denied';
  }
  try { await registerToken(barberId, barberName); } catch (e) { console.warn('[barberPush] enable failed:', e); }
  return 'granted';
}

/** Silent re-register on PIN-in for a device that ALREADY granted permission (no prompt). */
export async function initBarberPush(barberId: string, barberName?: string): Promise<void> {
  try {
    if (!barberId || barberPushState() !== 'granted') return;
    await registerToken(barberId, barberName);
  } catch (e) { console.warn('[barberPush] init failed (non-fatal):', e); }
}

/** Drop this device's token (e.g. on tap-out) so a barber stops getting another barber's alerts. */
export async function clearBarberPush(): Promise<void> {
  try {
    const t = localStorage.getItem('slik_barber_push_token');
    if (!t) return;
    // H30 FIX (Jose 2026-10-29 audit): the old code swallowed a delete failure (`.catch(()=>{})`) and
    // STILL cleared localStorage — leaving the stale token in Firestore forever. The next barber on this
    // device registers a new token, so when the departed barber's alert fires the Cloud Function can push
    // to the WRONG device (cross-barber leak). Now: only forget the local token if the server delete
    // actually succeeds; on failure keep it so the next tap-out (or init) retries the cleanup.
    try {
      await deleteDoc(doc(db, 'barberPushTokens', t));
      localStorage.removeItem('slik_barber_push_token');
    } catch (e) {
      console.warn('[barberPush] token delete failed — keeping local token to retry cleanup next tap-out:', e);
    }
  } catch { /* non-fatal */ }
}
